import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';

function TermsAndConditions() {
  const textColor = '#E7E8E9';
  const sectionStyle = { mb: 4 };
  const headingStyle = { color: textColor, fontWeight: 500, mb: 2 };
  const paragraphStyle = { color: textColor, mb: 2 };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: textColor }}>
        Seekr Terms and Conditions
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{ color: textColor }}>
        Last Updated: June 7, 2025
      </Typography>
      
      <Paper elevation={0} sx={{ p: 3, borderColor: '#9F6D47', borderWidth: '2px', borderStyle: 'solid' }}>
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            Introduction
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Welcome to Seekr! These Terms and Conditions ("Terms") govern your use of the Seekr
            application, website, and services (collectively, the "Services") provided by SEEKR, a
            company duly organized and existing under the laws of United States, with its registered
            office in New York and corporate domicile at 1820 Avenue M, 837 Brooklyn, NY 11230
            ("we," "us," or "our").
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            When you register with Seekr, we understand that you are choosing to place your
            wellbeing in our hands. We take that responsibility seriously. It therefore matters how
            you access our services and how we handle your information. We'd like to take this
            opportunity to explain what we are setting out to deliver as a company and what you
            can reasonably expect from us. Please take a few moments to read our terms and
            conditions.
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            By accessing or using the Services, you acknowledge that you have read these Terms,
            that these Terms govern your use of the Services, and that you agree to them. If you do
            not agree to be bound by these Terms, please do not use the Services.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            1. Service Description
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Seekr is a mental wellbeing app that provides access to daily guided rituals and 28-day
            transformational journeys for women who are going through life transitions. Our
            Services include, but are not limited to:
          </Typography>
          <Typography component="ul" sx={{ color: textColor, pl: 4 }}>
            <li>Guided meditation and mindfulness exercises</li>
            <li>Transformational journeys</li>
            <li>Daily rituals</li>
            <li>Wellbeing content</li>
            <li>Community features</li>
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            2. Account Registration and Eligibility
          </Typography>
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            2.1 Account Registration
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            To access the Services, you must register for an account. You can do this via our website,
            our app, or via certain third-party social networking services. To register, you need to
            provide certain personal information, including but not limited to, your name, email
            address, and other information as indicated on the registration form.
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You are responsible for maintaining the confidentiality of your account and password
            and for restricting access to your device. You may not register for more than one
            account. You agree to provide accurate, current, and complete information during the
            registration process and to update such information to keep it accurate, current, and
            complete.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            2.2 Eligibility
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You must be at least 16 years of age to use the Services. If you are under the age of
            majority where you live, you may only use the Services if your parent or guardian agrees
            to these Terms. If you are a parent or legal guardian of a user under the age of majority
            where you live, you are subject to these Terms and responsible for your child's activity
            on the Services.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            3. Subscriptions and Payments
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.1 Subscription Options
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We offer various subscription options to access our Services. Details of our current
            subscription plans, including pricing, are available on our website and in our app.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.2 Free Trial
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We may offer a free trial period for new users. You are only allowed one free trial. We
            reserve the right to determine eligibility for free trials. Free trials automatically convert
            to a paid subscription after the trial period unless canceled before the end of the trial
            period.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.3 Automatic Renewal
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            When you purchase a subscription, you authorize us to charge your payment method on
            a recurring basis for the subscription period you select (monthly or annual). Your
            subscription will automatically renew at the end of each subscription period unless you
            cancel it before the renewal date.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.4 Cancellation
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You may cancel your subscription at any time, but you must cancel before it renews to
            avoid being charged for the next subscription period. To cancel, go to your account
            settings in the app or on our website, or contact us at support@seekrwellness.com.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.5 No Refunds
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We do not provide refunds for any partial subscription periods. Once you have paid for a
            subscription period, you will have access to the Services for the remainder of that period
            regardless of when you cancel.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            3.6 Price Changes
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We reserve the right to change our subscription prices. Any price changes will apply to
            the next billing cycle after we have notified you.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            4. User Conduct and Prohibited Activities
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            4.1 User Conduct
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You are solely responsible for your conduct while using the Services. You agree to use
            the Services only for lawful purposes and in accordance with these Terms.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            4.2 Prohibited Activities
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You agree not to:
          </Typography>
          <Typography component="ul" sx={{ color: textColor, pl: 4 }}>
            <li>Use the Services in any way that violates any applicable law or regulation</li>
            <li>Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity</li>
            <li>Interfere with or disrupt the Services or servers or networks connected to the Services</li>
            <li>Attempt to gain unauthorized access to any portion of the Services or any other systems or networks connected to the Services</li>
            <li>Use the Services for any commercial purpose without our prior written consent</li>
            <li>Collect or store personal data about other users without their consent</li>
            <li>Use the Services in any way that could harm, disable, overburden, or impair the Services</li>
            <li>Upload, post, or otherwise transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
            <li>Attempt to decompile, reverse engineer, disassemble, or hack the Services, or defeat or overcome any of our encryption technologies or security measures</li>
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            5. Intellectual Property Rights
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            5.1 Our Intellectual Property
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            The Services, including all content, features, and functionality, are owned by Seekr and
            are protected by copyright, trademark, and other intellectual property laws. These
            Terms do not grant you any right, title, or interest in the Services or our content.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            5.2 Limited License
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
            non-transferable, non-sublicensable, and revocable license to access and use the
            Services solely for your personal, non-commercial purposes.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            5.3 User Content
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You retain all rights to any content you submit, post, or display on or through the
            Services ("User Content"). By submitting User Content, you grant us a worldwide, nonexclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate,
            distribute, and display such User Content in connection with providing and promoting
            the Services.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            6. Privacy
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Your privacy is important to us. Our <Link href="/privacy-policy" sx={{ color: textColor }}>Privacy Policy</Link>, which is incorporated into these
            Terms by reference, explains how we collect, use, and disclose information about you.
            By using the Services, you consent to the collection, use, and disclosure of your
            information as described in our <Link href="/privacy-policy" sx={{ color: textColor }}>Privacy Policy</Link>.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            7. Data Protection
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            7.1 Data Collection
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We collect the following types of information: - Name and email address - IP address -
            App usage analytics - Responses to onboarding questionnaires and preferences
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            7.2 Data Use
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We use your data to: - Provide, maintain, and improve the Services - Personalize your
            experience - Communicate with you about the Services - Monitor and analyze trends,
            usage, and activities in connection with the Services - Detect, investigate, and prevent
            fraudulent transactions and other illegal activities
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            7.3 Data Security
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We implement appropriate technical and organizational measures to protect your
            personal information against unauthorized or unlawful processing, accidental loss,
            destruction, or damage.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            7.4 Data Retention
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We retain your personal information for as long as necessary to fulfill the purposes for
            which we collected it, including for the purposes of satisfying any legal, accounting, or
            reporting requirements.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            7.5 Your Rights
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Depending on your location, you may have certain rights regarding your personal
            information, such as the right to access, correct, delete, or restrict processing of your
            personal information. To exercise these rights, please contact us at
            privacy@seekrwellness.com.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            8. Disclaimers
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            8.1 General Disclaimer
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
            ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED
            OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICES OR THE
            SERVERS THAT MAKE THEM AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL
            COMPONENTS.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            8.2 Wellbeing Disclaimer
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Seekr's meditation, mindfulness, and wellbeing content should not be considered health
            care, medical advice, therapy, or a substitute for professional medical care. The Services
            are not intended to diagnose, treat, cure, or prevent any disease or medical condition.
            Always seek the advice of your physician or other qualified health provider with any
            questions you may have regarding a medical condition.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            8.3 Not a Medical Service
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Seekr is not a licensed medical care provider and does not engage in, and has no
            expertise in, diagnosing, examining, or treating medical conditions of any kind, or in
            determining the effect of any specific exercise or practice on a medical condition.
          </Typography>
          
          <Typography variant="h6" sx={{ color: textColor, fontWeight: 500, mt: 2, mb: 1 }}>
            8.4 Use at Your Own Risk
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Not all activities described as part of the Services are suitable for everyone. Do not use
            the Services while driving, operating heavy machinery, or performing other tasks that
            require attention and concentration. You are solely responsible for your use of the
            Services.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            9. Limitation of Liability
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SEEKR,
            ITS AFFILIATES, OR THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS
            BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
            DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL,
            OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR
            INABILITY TO ACCESS OR USE THE SERVICES; (II) ANY CONDUCT OR CONTENT OF ANY
            THIRD PARTY ON THE SERVICES; (III) ANY CONTENT OBTAINED FROM THE SERVICES; AND
            (IV) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR
            CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
            NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN
            INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE
            AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING THE SERVICES DURING THE TWELVE (12)
            MONTHS PRECEDING THE EVENT GIVING RISE TO THE LIABILITY.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            10. Indemnification
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            You agree to defend, indemnify, and hold harmless Seekr, its affiliates, and their
            respective officers, directors, employees, and agents from and against any claims,
            liabilities, damages, losses, and expenses, including, without limitation, reasonable legal
            and accounting fees, arising out of or in any way connected with your access to or use of
            the Services, your violation of these Terms, or your violation of any rights of another.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            11. Termination
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We may terminate or suspend your account and access to the Services immediately,
            without prior notice or liability, for any reason, including, without limitation, if you
            breach these Terms. Upon termination, your right to use the Services will immediately
            cease.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            12. Changes to Terms
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            We reserve the right to modify these Terms at any time. If we make material changes to
            these Terms, we will notify you by email or by posting a notice on our website or app.
            Your continued use of the Services after such modifications will constitute your
            acknowledgment of the modified Terms and agreement to abide and be bound by them.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            13. Governing Law
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            These Terms shall be governed by and construed in accordance with the laws of the
            United States, without regard to its conflict of law provisions.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            14. Dispute Resolution
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Any dispute arising out of or relating to these Terms or the Services shall be resolved
            through binding arbitration in accordance with the rules of the American Arbitration
            Association. The arbitration shall be conducted in [Your Location], and the judgment of
            the arbitrator may be entered in any court having jurisdiction thereof.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            15. Severability
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            If any provision of these Terms is held to be invalid or unenforceable, such provision
            shall be struck and the remaining provisions shall be enforced to the fullest extent under
            law.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box sx={sectionStyle}>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            16. Entire Agreement
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            These Terms, together with the <Link href="/privacy-policy" sx={{ color: textColor }}>Privacy Policy</Link>, constitute the entire agreement between
            you and Seekr regarding the Services and supersede all prior agreements and
            understandings, whether written or oral.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: '#9F6D47' }} />
        
        <Box>
          <Typography variant="h5" gutterBottom sx={headingStyle}>
            17. Contact Information
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            If you have any questions about these Terms, please contact us at:
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Email: <Link href="mailto:legal@seekrwellness.com" sx={{ color: textColor }}>legal@seekrwellness.com</Link>
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Address: 1820 Avenue M, 837 Brooklyn, NY 11230
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Website: <Link href="https://www.seekrwellness.com" sx={{ color: textColor }}>www.seekrwellness.com</Link>
          </Typography>
          <Typography paragraph sx={paragraphStyle}>
            Thank you for using Seekr!
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default TermsAndConditions;